import React, { useState } from "react";
import _ from "lodash";

import { Button } from "@magic-engineering/components";

import AverageRating from "../AverageRating/AverageRating";
import styles from "./AssistantProfileCard.module.scss";
import { ReactComponent as ResumeIcon } from "../../assets/images/resume.svg";
import emptyAvatar from "../../assets/images/avatar-empty.svg";

const sectionClass = [styles.BaseMargin, styles.ProfileSection].join(" ");

function displayBreaklines(content, className) {
  return _.map(content.split(/\r?\n/), (paragraph, index) => (
    <p className={className} key={index}>
      {paragraph}
    </p>
  ));
}

const NoData = () => (
  <div className={sectionClass}>
    <h4 className={styles.SectionHeader}>No profile provided</h4>
    <p>Ask your assistant to fill out their profile so you can learn more about them.</p>
  </div>
);

const Ribbon = ({ className, content }) => {
  const ribbonClasses = _([styles.Ribbon, className]).compact().join(" ");
  return (
    <div className={ribbonClasses}>
      <h4>{content}</h4>
    </div>
  );
};

const Header = ({ assistant, expandable, showFull, setShowFull, history, feedbackUrl }) => {
  let rating;

  if (_.has(assistant, "rating") && _.has(assistant, "rating_count")) {
    rating = (
      <AverageRating
        average={assistant.rating_count >= 3 ? assistant.rating : 0}
        count={assistant.rating_count || 0}
      />
    );

    if (history && feedbackUrl) {
      rating = (
        <div role="button" onClick={history && feedbackUrl && (() => history.push(feedbackUrl))}>
          {rating}
        </div>
      );
    }
  }

  return (
    <div className={[styles.BaseMargin, styles.Header].join(" ")}>
      {assistant.photo_url ? (
        <img src={assistant.photo_url} className={styles.AssistantPhoto} alt="" />
      ) : (
        <div className={`${styles.AssistantPhoto} ${styles.NoPhoto}`}>
          <img src={emptyAvatar} alt="" />
        </div>
      )}
      <div className={styles.HeaderTitle}>
        {rating}
        <h1>{assistant?.first_name}</h1>
        <div className={styles.Subheader}>
          <h2>{assistant?.last_name}</h2>
          {expandable && !showFull && (
            <Button
              label="Full Profile"
              type="PrimaryInverse"
              onClick={() => setShowFull(true)}
              className={styles.ShowFullButton}
              dataTestId="ShowFullButton"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Bio = ({ content }) => (
  <div className={styles.Bio}>{displayBreaklines(content, styles.BaseMargin)}</div>
);

const WorkExperiences = ({ workExperiences }) => (
  <div className={sectionClass}>
    <h4 className={styles.SectionHeader}>Work Experience</h4>
    <ul>
      {workExperiences.map((experience, index) => (
        <li key={index} className={styles.Experience}>
          <div className={styles.ExperienceHeader}>
            <span className={styles.Role}>{experience.role}</span>
            {experience.tenure && experience.tenure}
          </div>
          {experience.overview && displayBreaklines(experience.overview)}
        </li>
      ))}
    </ul>
  </div>
);

const Skills = ({ skills }) => (
  <div className={sectionClass}>
    <h4 className={styles.SectionHeader}>Skills</h4>
    <ul>
      {skills.map((skill, index) => (
        <li key={index}>
          <span className={styles.Skill}>{skill.name}</span>
        </li>
      ))}
    </ul>
  </div>
);

const Tools = ({ tools }) => (
  <div className={sectionClass}>
    <h4 className={styles.SectionHeader}>Tools</h4>
    <ul>
      {tools.map((tool, index) => (
        <span key={index} className={styles.ToolItem}>
          <li>
            <span className={styles.Tool}>{tool.name}</span>
          </li>
        </span>
      ))}
    </ul>
  </div>
);

const Resume = ({ firstName, lastName, url }) => {
  // get file extension from file URI
  // ie: https://magic-dedicated.s3.ap-northeast-1.amazonaws.com/local/uploads/1661918793527/170b626a-f942-410d-ab0f-6feaf94547c9/ba893bd2-4b0d-4c27-8499-62b3e1de61e0.pdf
  // will return pdf
  const fileExtension = url.split(".").pop();
  const fileName = `${firstName} ${lastName}.${fileExtension}`;

  return (
    <div className={sectionClass}>
      <h4 className={styles.SectionHeader}>Resume</h4>
      <p>
        <ResumeIcon /> &nbsp;
        <a href={url} target="_blank" rel="noreferrer" download={fileName}>
          {fileName}
        </a>
      </p>
    </div>
  );
};

const Availability = ({ assistant, isWorkingForClient }) => {
  const currentWorkingHours = Number.parseFloat(assistant.current_working_hours);
  const remainingHours = Math.max(assistant.hours_per_week - currentWorkingHours, 0);
  let isWorkingWithOtherClients =
    (!isWorkingForClient && currentWorkingHours > 0) ||
    (isWorkingForClient && currentWorkingHours !== assistant.default_hours);

  const availabilityMessage = (
    <p data-testid="AvailabilityMessage">
      {isWorkingForClient ? (
        <>
          Working for you{" "}
          <span className={styles.Availability}>{assistant.default_hours} hours per week</span>
        </>
      ) : (
        <>
          Available for hire{" "}
          <span className={styles.Availability}>{remainingHours} hours per week</span>
        </>
      )}
      &nbsp;
      {isWorkingWithOtherClients ? (
        <>(currently working with other clients)</>
      ) : (
        <>(currently working with no other clients)</>
      )}
    </p>
  );

  return (
    <div className={sectionClass}>
      <h4 className={styles.SectionHeader}>Availability</h4>
      {availabilityMessage}
    </div>
  );
};

const CollapseButton = ({ onClick }) => (
  <div className={`${styles.BaseMargin} ${styles.CollapseButtonSection}`}>
    <Button
      label="Close Profile"
      type="PrimaryInverse"
      onClick={() => onClick(false)}
      className={styles.CollapseButton}
      dataTestId="CollapseButton"
    />
  </div>
);

const AssistantProfileCard = ({
  assistant,
  client,
  top,
  bottom,
  ribbonContent,
  ribbonClass,
  expandable,
  startExpanded,
  history,
  feedbackUrl,
}) => {
  const [showFull, setShowFull] = useState(expandable ? startExpanded : true);

  if (!assistant) {
    return null;
  }

  const isWorkingForClient =
    client && client.mongo_client_id && client.mongo_client_id === assistant.mongo_client_id;
  const hasBio = _.get(assistant, "description.length", 0) > 0;
  const hasWorkExperiences = _.get(assistant, "work_experiences.length", 0) > 0;
  const hasSkills = _.get(assistant, "skills.length", 0) > 0;
  const hasAvailability = isWorkingForClient || _.get(assistant, "hours_per_week", 0) > 0;
  const hasTools = _.get(assistant, "tools.length", 0) > 0;
  const hasResume = !_.isNil(assistant?.resume_url);

  return (
    <div className={styles.AssistantProfileCard} data-testid="AssistantProfileCard">
      {top && <div className={styles.BaseMargin}>{top}</div>}
      {ribbonContent && <Ribbon className={ribbonClass} content={ribbonContent} />}
      <Header
        assistant={assistant}
        expandable={expandable}
        showFull={showFull}
        setShowFull={setShowFull}
        history={history}
        feedbackUrl={feedbackUrl}
      />
      {showFull && (
        <>
          {hasBio ? <Bio content={assistant.description} /> : <div className={sectionClass}></div>}
          {hasWorkExperiences && <WorkExperiences workExperiences={assistant.work_experiences} />}
          {hasSkills && <Skills skills={assistant.skills} />}
          {hasTools && <Tools tools={assistant.tools} />}
          {isWorkingForClient && !hasWorkExperiences && !hasSkills && !hasTools && <NoData />}
          {hasAvailability && (
            <Availability assistant={assistant} isWorkingForClient={isWorkingForClient} />
          )}
          {hasResume && (
            <Resume
              url={assistant.resume_url}
              firstName={assistant.first_name}
              lastName={assistant.last_name}
            />
          )}
          {expandable && <CollapseButton onClick={setShowFull} />}
        </>
      )}
      {bottom && <div className={styles.BaseMargin}>{bottom}</div>}
    </div>
  );
};

export default AssistantProfileCard;
